/**
 * Admin — Position editor: the permission checkbox tree. Feature groups with
 * tri-state master toggles → items → per-item R/W/E/D/A/G bits. Grants locked
 * by the model bye-laws render with an amber lock + the exact bye-law citation
 * and cannot be changed (the API also refuses with 409). Members section
 * assigns/removes holders. Route: /admin/positions/[id] ('new' creates).
 * Wired: GET /api/positions/registry, GET/PATCH/POST /api/positions[/<id>],
 * POST /api/positions/<id>/members[/<uid>/remove], POST /api/positions/<id>/delete.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Avatar, Button, Card, EmptyState, Field, Hairline, Input, Pill,
  Screen, Sheet, Skeleton, TopBar,
} from '@/ui';

const BITS = ['read', 'write', 'edit', 'delete', 'approve', 'grant'] as const;
type Bit = (typeof BITS)[number];
const BIT_LETTER: Record<Bit, string> = {
  read: 'R', write: 'W', edit: 'E', delete: 'D', approve: 'A', grant: 'G',
};

type RegistryItem = { key: string; label: string; sub: string; citation: string };
type RegistryGroup = { key: string; label: string; items: RegistryItem[] };
type Grant = Record<Bit, boolean> & { lockedByLaw?: boolean; ruleSource?: string; citation?: string };
type Member = { id: number; fullName: string; role: string; flat: string | null };
type DirectoryUser = { id: number; fullName: string; role: string; flat?: { code?: string } };

const emptyGrant = (): Grant =>
  ({ read: false, write: false, edit: false, delete: false, approve: false, grant: false });

function BitBox({ bit, on, locked, onPress }: {
  bit: Bit; on: boolean; locked?: boolean; onPress: () => void;
}) {
  const { colors } = useTheme();
  const bg = on ? (locked ? colors.accent100 : colors.primary500) : 'transparent';
  const border = on ? (locked ? colors.accent500 : colors.primary500) : colors.border;
  const fg = on ? (locked ? colors.accent700 : colors.canvas0) : colors.textTertiary;
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: on, disabled: !!locked }}
      accessibilityLabel={`${bit}${locked ? ' (locked by law)' : ''}`}
      onPress={onPress}
      style={{
        flex: 1, minHeight: 44, borderRadius: radius.md, borderWidth: 1,
        borderColor: border, backgroundColor: bg,
        alignItems: 'center', justifyContent: 'center',
        opacity: locked && !on ? 0.35 : 1,
      }}
    >
      <AppText variant="bodySm" weight="600" style={{ color: fg }}>{BIT_LETTER[bit]}</AppText>
    </Pressable>
  );
}

function GroupCheck({ state, onPress }: { state: 'on' | 'off' | 'mixed'; onPress: () => void }) {
  const { colors } = useTheme();
  const on = state !== 'off';
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: state === 'mixed' ? 'mixed' : state === 'on' }}
      accessibilityLabel="Toggle whole group"
      onPress={onPress}
      hitSlop={10}
      style={{
        width: 24, height: 24, borderRadius: 7, borderWidth: 1.5,
        borderColor: on ? colors.primary500 : colors.border,
        backgroundColor: state === 'on' ? colors.primary500 : 'transparent',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      {state === 'on' ? <Icon name="check" size={14} color={colors.canvas0} /> : null}
      {state === 'mixed' ? (
        <View style={{ width: 10, height: 2.5, borderRadius: 2, backgroundColor: colors.primary500 }} />
      ) : null}
    </Pressable>
  );
}

export default function PositionEditorScreen() {
  const { colors } = useTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const isNew = id === 'new';

  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [groups, setGroups] = React.useState<RegistryGroup[]>([]);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isTemplate, setIsTemplate] = React.useState(false);
  const [grants, setGrants] = React.useState<Record<string, Grant>>({});
  const [members, setMembers] = React.useState<Member[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [directory, setDirectory] = React.useState<DirectoryUser[] | null>(null);
  const [query, setQuery] = React.useState('');
  const [assignBusy, setAssignBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const reg = await api.get<{ groups: RegistryGroup[] }>('/api/positions/registry');
      if (id !== 'new') {
        const d = await api.get<{
          name: string; description: string; isTemplate: boolean;
          grants: (Grant & { featureKey: string })[]; members: Member[];
        }>(`/api/positions/${id}`);
        setName(d.name);
        setDescription(d.description ?? '');
        setIsTemplate(!!d.isTemplate);
        const g: Record<string, Grant> = {};
        for (const row of d.grants ?? []) g[row.featureKey] = row;
        setGrants(g);
        setMembers(d.members ?? []);
      }
      setGroups(reg.groups ?? []);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [id]);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const toggleBit = (featureKey: string, bit: Bit, citation?: string) => {
    const cur = grants[featureKey];
    if (cur?.lockedByLaw) {
      setNotice({ kind: 'err', text: citation ? `Locked by law — ${citation}` : 'This grant is mandated by the bye-laws.' });
      return;
    }
    setNotice(null);
    setGrants((s) => {
      const next = { ...(s[featureKey] ?? emptyGrant()) };
      next[bit] = !next[bit];
      // Anything beyond read implies read — a writer who can't see is nonsense.
      if (bit !== 'read' && next[bit]) next.read = true;
      return { ...s, [featureKey]: next };
    });
  };

  const groupState = (grp: RegistryGroup): 'on' | 'off' | 'mixed' => {
    const rows = grp.items.map((it) => grants[it.key]);
    const full = rows.every((g) => g?.read && g?.write && g?.edit);
    if (full) return 'on';
    return rows.some((g) => g && BITS.some((b) => g[b])) ? 'mixed' : 'off';
  };

  const toggleGroup = (grp: RegistryGroup) => {
    setNotice(null);
    const turnOn = groupState(grp) !== 'on';
    setGrants((s) => {
      const next = { ...s };
      for (const it of grp.items) {
        const cur = next[it.key];
        if (cur?.lockedByLaw) continue; // law wins — group toggle skips locked rows
        if (turnOn) next[it.key] = { ...(cur ?? emptyGrant()), read: true, write: true, edit: true };
        else next[it.key] = emptyGrant();
      }
      return next;
    });
  };

  const save = async () => {
    if (name.trim().length < 2) {
      setNotice({ kind: 'err', text: 'Give the position a name first.' });
      return;
    }
    setSaving(true);
    setNotice(null);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      grants: Object.entries(grants)
        .filter(([, g]) => !g.lockedByLaw && BITS.some((b) => g[b]))
        .map(([featureKey, g]) => ({
          featureKey,
          ...Object.fromEntries(BITS.map((b) => [b, !!g[b]])),
        })),
    };
    try {
      if (isNew) {
        await api.post<unknown>('/api/positions', payload);
        router.replace('/admin/positions');
      } else {
        // Templates keep their statutory name — don't send it.
        const { name: _n, ...rest } = payload;
        await api.patch<unknown>(`/api/positions/${id}`, isTemplate ? rest : payload);
        setNotice({ kind: 'ok', text: 'Saved.' });
      }
    } catch (e) {
      const err = e as { error?: string; citation?: string };
      const msg = err?.error ?? 'Could not save. Try again.';
      setNotice({ kind: 'err', text: err?.citation ? `${msg} — ${err.citation}` : msg });
    } finally {
      setSaving(false);
    }
  };

  const removePosition = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    try {
      await api.post<unknown>(`/api/positions/${id}/delete`, {});
      router.replace('/admin/positions');
    } catch {
      setNotice({ kind: 'err', text: 'Could not delete this position.' });
      setConfirmDelete(false);
    }
  };

  const openPicker = () => {
    setPickerOpen(true);
    setQuery('');
    if (directory === null) {
      api.get<{ items: DirectoryUser[] }>('/api/admin/users?per=200')
        .then((body) => setDirectory(body.items ?? []))
        .catch(() => setDirectory([]));
    }
  };

  const assign = (userId: number) => {
    setAssignBusy(true);
    api.post<{ members: Member[] }>(`/api/positions/${id}/members`, { userId })
      .then((body) => { setMembers(body.members ?? []); setPickerOpen(false); })
      .catch((e) => {
        setNotice({ kind: 'err', text: (e as { error?: string })?.error ?? 'Could not assign.' });
        setPickerOpen(false);
      })
      .finally(() => setAssignBusy(false));
  };

  const unassign = (userId: number) => {
    api.post<{ members: Member[] }>(`/api/positions/${id}/members/${userId}/remove`, {})
      .then((body) => setMembers(body.members ?? []))
      .catch(() => setNotice({ kind: 'err', text: 'Could not remove the holder.' }));
  };

  const memberIds = new Set(members.map((m) => m.id));
  const candidates = (directory ?? []).filter((u) => !memberIds.has(u.id))
    .filter((u) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return u.fullName.toLowerCase().includes(q) || (u.flat?.code ?? '').toLowerCase().includes(q);
    });

  if (state === 'loading') {
    return (
      <Screen scroll={false}>
        <TopBar title={isNew ? 'New position' : 'Position'} onBack={goBack} />
        <Screen padded style={{ paddingTop: space.s4 }}>
          <View style={{ gap: space.s3 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} h={72} />)}</View>
        </Screen>
      </Screen>
    );
  }
  if (state === 'error') {
    return (
      <Screen scroll={false}>
        <TopBar title="Position" onBack={goBack} />
        <EmptyState
          icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
          title="Couldn't load this position"
          body="Check your connection and try again."
          cta="Retry"
          onCta={() => { void load(); }}
        />
      </Screen>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title={isNew ? 'New position' : name} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {isTemplate ? (
          <View style={{
            flexDirection: 'row', alignItems: 'center', gap: space.s2,
            borderRadius: radius.lg, backgroundColor: colors.canvas1,
            padding: space.s3, marginBottom: space.s4,
          }}>
            <Icon name="shield" size={16} color={colors.primary700} />
            <AppText variant="bodySm" tone="secondary" style={{ flex: 1 }}>
              Statutory position under the Maharashtra model bye-laws. Locked grants show
              the bye-law that mandates them and can&apos;t be unticked.
            </AppText>
          </View>
        ) : null}

        <View style={{ gap: space.s4 }}>
          {isNew || !isTemplate ? (
            <Field label="Name">
              <Input value={name} onChangeText={setName} placeholder="e.g. Security Chair" maxLength={100} />
            </Field>
          ) : null}
          <Field label="Description" help={isNew ? 'What this position is for — holders see this.' : undefined}>
            <Input value={description} onChangeText={setDescription} placeholder="What can holders do?" maxLength={500} multiline />
          </Field>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: space.s5, marginBottom: space.s2 }}>
          <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>Permissions</AppText>
          <AppText variant="bodySm" tone="tertiary">
            R read · W create · E edit · D delete · A approve · G delegate
          </AppText>
        </View>

        <View style={{ gap: space.s3 }}>
          {groups.map((grp) => (
            <Card key={grp.key}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                <GroupCheck state={groupState(grp)} onPress={() => toggleGroup(grp)} />
                <AppText variant="bodyMd" weight="600" style={{ flex: 1 }}>{grp.label}</AppText>
              </View>
              {grp.items.map((it, idx) => {
                const g = grants[it.key];
                const locked = !!g?.lockedByLaw;
                return (
                  <View key={it.key}>
                    {idx === 0 ? <View style={{ height: space.s3 }} /> : <Hairline />}
                    <View style={{ paddingVertical: space.s3 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <AppText variant="bodyMd" weight="500" style={{ flexShrink: 1 }}>{it.label}</AppText>
                        {locked ? <Icon name="lock" size={13} color={colors.accent700} /> : null}
                      </View>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 1 }}>{it.sub}</AppText>
                      <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s3 }}>
                        {BITS.map((b) => (
                          <BitBox
                            key={b}
                            bit={b}
                            on={!!g?.[b]}
                            locked={locked}
                            onPress={() => toggleBit(it.key, b, g?.citation)}
                          />
                        ))}
                      </View>
                      {locked && g?.citation ? (
                        <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
                          <Icon name="lock" size={12} color={colors.accent700} />
                          <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>
                            {g.citation}
                          </AppText>
                        </View>
                      ) : null}
                    </View>
                  </View>
                );
              })}
            </Card>
          ))}
        </View>

        {!isNew ? (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: space.s5, marginBottom: space.s2 }}>
              <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>
                Holders · {members.length}
              </AppText>
              <SmallButton label="Add holder" icon="user-plus" onPress={openPicker} />
            </View>
            {members.length === 0 ? (
              <AppText variant="bodySm" tone="tertiary">Nobody holds this position yet.</AppText>
            ) : (
              <Card>
                {members.map((m, idx) => (
                  <View key={m.id}>
                    {idx > 0 ? <Hairline /> : null}
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s2 }}>
                      <Avatar name={m.fullName} size={36} />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <AppText variant="bodyMd" weight="500">{m.fullName}</AppText>
                        <AppText variant="bodySm" tone="tertiary">
                          {[m.flat, m.role].filter(Boolean).join(' · ')}
                        </AppText>
                      </View>
                      <SmallButton label="Remove" danger onPress={() => unassign(m.id)} />
                    </View>
                  </View>
                ))}
              </Card>
            )}
          </>
        ) : null}

        {notice ? (
          <AppText
            variant="bodySm"
            weight="500"
            style={{ marginTop: space.s4, color: notice.kind === 'ok' ? colors.primary700 : colors.danger500 }}
          >
            {notice.text}
          </AppText>
        ) : null}

        <View style={{ marginTop: space.s4, gap: space.s3, marginBottom: space.s6 }}>
          <Button
            title={saving ? 'Saving…' : isNew ? 'Create position' : 'Save changes'}
            disabled={saving}
            onPress={() => { void save(); }}
          />
          {!isNew && !isTemplate ? (
            <Button
              title={confirmDelete ? 'Tap again to delete' : 'Delete position'}
              kind="secondary"
              onPress={() => { void removePosition(); }}
            />
          ) : null}
        </View>
      </Screen>

      <Sheet visible={pickerOpen} onClose={() => setPickerOpen(false)}>
        <AppText variant="headingSm" style={{ marginBottom: space.s3 }}>Add holder</AppText>
        <Input value={query} onChangeText={setQuery} placeholder="Search name or flat" autoFocus />
        <ScrollView style={{ maxHeight: 360, marginTop: space.s3 }} keyboardShouldPersistTaps="handled">
          {directory === null ? (
            <View style={{ gap: space.s2 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={48} />)}</View>
          ) : candidates.length === 0 ? (
            <AppText variant="bodySm" tone="tertiary" style={{ paddingVertical: space.s3 }}>
              No matching members.
            </AppText>
          ) : (
            candidates.slice(0, 40).map((u) => (
              <Pressable
                key={u.id}
                accessibilityRole="button"
                disabled={assignBusy}
                onPress={() => assign(u.id)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 10, opacity: assignBusy ? 0.5 : 1 }}
              >
                <Avatar name={u.fullName} size={36} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="500">{u.fullName}</AppText>
                  <AppText variant="bodySm" tone="tertiary">
                    {[u.flat?.code, u.role].filter(Boolean).join(' · ')}
                  </AppText>
                </View>
                <Pill status="pending" label="ADD" />
              </Pressable>
            ))
          )}
        </ScrollView>
      </Sheet>
    </Screen>
  );
}
